import React, { useContext, useEffect } from 'react'
import { Grid, Typography } from '@mui/material';
import { Control, FieldErrors, UseFormSetValue, useForm } from "react-hook-form";
import { ControlledInput } from '../../components/forms/input/ControlledInput';
import { endpoints } from '../../api/endpoints';
import { useCrudForm } from '../../api/useCrudForm';
import AddEditModal from '../../components/modal/Modal';
import { ModalFooter } from '../../components/modal/ModalFooter';
import CustomSelect from '../../components/forms/select/CustomSelect';
import { toast } from 'react-toastify';
import * as Yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { AppContext } from "../../contexts/AppContext";

export default function AddEditUserModal({ refetchData, open, setOpen, setSelectedItem, selectedItem }: { refetchData: any, open: boolean, setOpen: (value: boolean) => void, selectedItem: any, setSelectedItem: (value: any) => void }) {

    const [branchList, setBranchList] = React.useState<any | null>()
    const [designationList, setDesignationList] = React.useState<any | null>()
    const [roleList, setRoleList] = React.useState<any | null>()
    const { user } = useContext(AppContext)

    const { handleSubmit, control, formState: { errors, }, watch, setValue } = useForm(
        {
            resolver: yupResolver(selectedItem ? EditUserSchema : StoreUserSchema),
            defaultValues: selectedItem ?? {
                fullName: '',
                branchId: '',
                userName: '',
                roleId: '',
                empID: '',
                email: '',
                designationId: '',
            }
        }
    )

    const handleClose = () => {
        setOpen(false);
        setSelectedItem(null)
    };

    const onError = (err: any) => {
        setOpen(false);
        setSelectedItem(null)
        toast.error(err)
    }

    const cancelMessage = 'Cancel current unsaved changes and go back to user list?'

    const onSuccess = (values: any, id?: number) => {
        if (values.id) {
            refetchData()
            setOpen(false);
            setSelectedItem(null)

        } else {
            refetchData()
            setOpen(false);
            setSelectedItem(null)
        }

    }

    const { get } = useCrudForm();

    useEffect(() => {
        get({
            endpoints: endpoints.common.getBranches,
            onSuccess: (data: any) => {
                setBranchList(data.data)
            },
            onError: (error: any) => {
                console.log(error)
            }
        })
        get({
            endpoints: endpoints.common.getRoles,
            onSuccess: (data: any) => {
                setRoleList(data.data)
            },
            onError: (error: any) => {
                console.log(error)
            }
        })
        get({
            endpoints: endpoints.common.getDesignations,
            onSuccess: (data: any) => {
                setDesignationList(data.data)
            },
            onError: (error: any) => {
                console.log(error)
            }
        })
    }, [])

    return (
        <AddEditModal title={selectedItem ? 'Edit User' : 'Add New User'} setOpen={setOpen} setSelectedItem={setSelectedItem} open={open}>
            <PersonalInfo branches={branchList} designations={designationList} user={user} control={control} errors={errors} setValue={setValue} />
            <AccountDetail roleList={roleList} control={control} errors={errors} watch={watch} setValue={setValue} />
            <ModalFooter endpoint={endpoints.user.post} onSuccess={onSuccess} handleSubmit={handleSubmit} handleModalClose={handleClose} selectedItem={selectedItem} cancelMessage={cancelMessage} onError={onError} />
        </AddEditModal>
    );
}


const PersonalInfo = ({ branches, designations, user, control, errors, setValue }: { branches: any, designations: any, user: any, control: Control<any, any>, errors: FieldErrors<any>, setValue: UseFormSetValue<any> }) => {

    useEffect(() => {
        if (!user?.IsSuperAdmin) {
            setValue('branchId', user?.branchId)
        }
    }, [user])

    return (
        <>
            <Typography component={'p'} mt={2} mb={2}>Personal Information</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} >
                    <ControlledInput name="fullName" control={control} errors={errors} label="Fullname" />
                </Grid>
                {
                    user?.IsSuperAdmin &&
                    <Grid item xs={12} >
                        <CustomSelect
                            label="Branch"
                            name="branchId"
                            errors={errors?.branchId?.message}
                            control={control}
                            values={branches ?? []}
                            size={12}
                            setValue={setValue}
                            labelName="branchName"
                        />
                    </Grid>
                }
                <Grid item xs={6}>
                    <ControlledInput name="empID" control={control} errors={errors} label="Employee ID" />
                </Grid>
                <Grid item xs={6} >
                    <CustomSelect
                        label="Designation"
                        name="designationId"
                        errors={errors?.designationId?.message}
                        control={control}
                        values={designations ?? []}
                        size={12}
                    />
                </Grid>
                <Grid item xs={6}>
                    <ControlledInput name="email" control={control} errors={errors} label="Email" />
                </Grid>
            </Grid>
        </>

    )
}

const AccountDetail = ({ roleList, control, errors, watch, setValue }: { roleList: any, control: Control<any, any>, errors: FieldErrors<any>, watch: any, setValue: UseFormSetValue<any> }) => {

    const watchEmail: any = watch("email")

    useEffect(() => {
        if (watchEmail) {
            setValue('userName', watchEmail.split('@')[0])
        }
    }, [watchEmail])

    return (
        <>
            <Typography component={'p'} mt={4} mb={2}>Account Details</Typography>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <ControlledInput name="userName" control={control} errors={errors} label="Username" disabled={true} />
                </Grid>
                <Grid item xs={6}>
                    <CustomSelect
                        label="Role"
                        name="roleId"
                        errors={errors?.roleId?.message}
                        control={control}
                        values={roleList ?? []}
                        size={12}
                        setValue={setValue}
                        labelName="roleName"
                        isEnum={true}
                    />
                </Grid>
            </Grid>
        </>
    )
}


export const StoreUserSchema = Yup.object().shape({
    branchId: Yup.number().typeError('Invalid Branch').required('Required').notOneOf([0], 'Required'),
    designationId: Yup.number().typeError('Invalid Designation').required('Required').notOneOf([0], 'Required'),
    fullName: Yup.string().required("Fullname is Required"),
    roleId: Yup.number().typeError('Invalid Role')
        .required('Required').oneOf([0, 1, 2], 'Invalid Role'),
    userName: Yup.string().required("Username is Required").min(5, 'Username must be at least 5 characters'),
    email: Yup.string().email('Invalid Email')
        .matches(/@nepalbank\.com\.np$/, 'Must be @nepalbank.com.np')
        .required('Email is Required'),
    empID: Yup.string().required('Employee ID is Required'),
});

export const EditUserSchema = Yup.object().shape({
    branchId: Yup.number().typeError('Invalid Branch').required('Required').notOneOf([0], 'Required'),
    designationId: Yup.number().typeError('Invalid Designation').required('Required').notOneOf([0], 'Required'),
    fullName: Yup.string().required("Fullname is Required"),
    roleId: Yup.number().typeError('Invalid Role')
        .required('Required').oneOf([0, 1, 2], 'Invalid Role'),
    userName: Yup.string().required("Username is Required").min(5, 'Username must be at least 5 characters'),
    email: Yup.string().email('Invalid Email')
        .matches(/@nepalbank\.com\.np$/, 'Must be @nepalbank.com.np')
        .required('Email is Required'),
    empID: Yup.string().required('Employee ID is Required'),
});